import { datadogRum } from "@datadog/browser-rum";
import {
  addDays,
  differenceInDays,
  differenceInMinutes,
  differenceInMonths,
  differenceInYears,
  format,
  formatDuration,
  intervalToDuration,
  isBefore,
  isValid,
  isWithinInterval,
  max,
  min,
  parse as parseDate,
  parseISO
} from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { decodeHTML } from "entities";
import { saveAs } from "file-saver";
import * as htmlToImage from "html-to-image";
import {
  camelCase,
  isNil,
  omit,
  omitBy,
  snakeCase,
  truncate,
  values
} from "lodash";
import { parse, unparse } from "papaparse";

import { NON_BREAKABLE_SPACE } from "constants/common";
import { StaffMemberNameParts } from "services/types/staff.type";
import { convertSunoPlaceholdersToMergeTags } from "utils/suno-merge-tags.util";
import { viteEnv } from "utils/vite-env.util";

import { TITLES } from "../constants";

const DATE_MASK = "MM/dd/yyyy";

// A for-in key that is a run of digits ("0", "1", ...) means we are iterating an
// array, so the value is an error entry rather than a sibling object scalar like
// `code`. Use a strict digit test rather than `!Number.isNaN(Number(key))`, which
// would also accept "" / whitespace (Number("") === 0).
const isArrayIndex = (key: string) => /^\d+$/.test(key);

export const extractBackendErrorStringMessages = (
  response: any,
  result: Set<string>,
  includeFieldName: boolean = false,
  fieldName: string = ""
) => {
  if (typeof response !== "object") return;
  for (let propName in response) {
    if (
      response.hasOwnProperty(propName) &&
      response[propName] &&
      typeof response[propName] === "object"
    ) {
      extractBackendErrorStringMessages(
        response[propName],
        result,
        includeFieldName,
        isArrayIndex(propName) ? fieldName : propName
      );
    } else {
      const value = response[propName];
      // Capture a leaf string when it is a recognised DRF error message:
      //   - { message: "..." }  custom field error object ([{ message, code }])
      //   - { detail: "..." }   APIException / non-field default
      //   - ["plain string"]    DRF default field & non_field_errors arrays (propName is the index)
      const isErrorMessage =
        typeof value === "string" &&
        (propName === "message" ||
          propName === "detail" ||
          isArrayIndex(propName));
      if (isErrorMessage) {
        const message = `${includeFieldName ? `${fieldName}: ` : ""}${value}`;
        result.add(message);
      }
    }
  }
};

// Long enough for a real sentence, short enough that an HTML page or stack trace that happens
// to parse as JSON cannot become a dialog's copy.
const MAX_DISPLAYABLE_BACKEND_MESSAGE_LENGTH = 300;

// Shapes that are internals rather than copy: a URL, a stack frame, a serialized object,
// an HTTP status line, or an opaque id (UUID or a long unbroken token such as a provider's
// document id). The dialog must never show these whatever the backend sends (SN-19414 AC3),
// so a message that contains one is treated as "no usable message".
const INTERNAL_DETAIL_PATTERNS = [
  /https?:\/\//i,
  /Traceback|File "[^"]+", line \d+|\bat \S+ \(\S+:\d+:\d+\)/,
  /^\s*[[{]/,
  /^\s*(HTTP\/\d(\.\d)?\s+)?\d{3}\s+[A-Z][A-Za-z]+(\s+[A-Z][A-Za-z]+)*\s*$/,
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i,
  /[A-Za-z0-9_-]{24,}/
];

const looksLikeInternalDetail = (message: string): boolean =>
  INTERNAL_DETAIL_PATTERNS.some((pattern) => pattern.test(message));

/**
 * Pulls one displayable sentence out of an RTK Query error, or undefined if there isn't one.
 *
 * Endpoints with a custom `responseHandler` that throws on a non-ok response make RTK Query
 * report `status: "PARSING_ERROR"` and put the **raw response text** in `error.data` — it
 * clones the response and reads it alongside the handler, so the body does survive, just
 * unparsed. Endpoints without one get an already-parsed object. Both are handled here.
 *
 * Returns undefined rather than guessing whenever the body is not usable JSON (a Django debug
 * 500 arrives as HTML) or the message is implausibly long, so callers can fall back to their
 * own copy instead of showing the user a blob.
 */
export const readDisplayableBackendMessage = (
  error: unknown
): string | undefined => {
  const raw = (error as { data?: unknown } | undefined)?.data;
  let body: unknown = raw;

  if (typeof raw === "string") {
    try {
      body = JSON.parse(raw);
    } catch {
      return undefined;
    }
  }

  if (!body || typeof body !== "object") return undefined;

  const messages = new Set<string>();
  extractBackendErrorStringMessages(body, messages);

  const [first] = [...messages];
  const message = first?.trim();

  return message &&
    message.length <= MAX_DISPLAYABLE_BACKEND_MESSAGE_LENGTH &&
    !looksLikeInternalDetail(message)
    ? message
    : undefined;
};

export const hasStatusTransitionError = (errorMessage: string): boolean => {
  const regex = /Transition from (.+) to (.+) is not allowed/;
  return Boolean(errorMessage.match(regex));
};

export const isBlank = (
  value?: string | number | null | boolean | object
): boolean => {
  return (
    value === null ||
    value === undefined ||
    (typeof value === "object" && isBlankObject(value)) ||
    String(value).trim() === ""
  );
};

export const getFirstNonBlank = (
  values: Array<string | number | null | boolean | object | undefined>
) => {
  for (const value of values) {
    if (!isBlank(value)) {
      return value;
    }
  }
  return undefined;
};

export const getValueOrDefault = (
  value: string | number | null | boolean | object | undefined,
  defaultValue: string | number | null | boolean | object | undefined
) => (isBlank(value) ? defaultValue : value);

export const isNotBlank = (
  value?: string | number | null | boolean
): boolean => {
  return !isBlank(value);
};

export const isBlankObject = (value: any): boolean => {
  if (value === null || value === undefined) return true;

  if (Array.isArray(value) && (!value.length || !value.some(isNotBlank)))
    return true;

  return !values(value).some(isNotBlank);
};

export const isWithin24Hours = (dateStr: string): boolean => {
  const date = new Date(dateStr);
  const now = new Date();
  const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  return isWithinInterval(date, {
    start: twentyFourHoursAgo,
    end: now
  });
};

export const isInThePast = (dateStr: string): boolean => {
  const date = parseISO(dateStr);
  const now = new Date();
  return isBefore(date, now);
};

export const getDateTime = (date: Date, time: Date) =>
  new Date(`${format(date, "yyyy-MM-dd")} ${format(time, "HH:mm")}`);

export const formatDateToDateAndTime = (date: Date) =>
  `${format(date, "MM/dd/yy")} ${format(date, "hh:mmaaaaa'm'")}`;

export const formatDateInTimezone = (
  date: Date | string | number,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
) => {
  return zonedTimeToUtc(date, timezone);
};

export const convertDateInTimezoneToCurrent = (
  date: Date | string | number,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
) => {
  const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return utcToZonedTime(zonedTimeToUtc(date, currentTimeZone), timezone);
};

export const convertDateFromCurrentTimezoneToTimezone = (
  date: Date | string | number,
  newTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone
) => {
  const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const utcDate = zonedTimeToUtc(date, newTimezone);
  const zonedDate = utcToZonedTime(utcDate, currentTimeZone);
  return zonedDate;
};

export const getDaysInDateTimeInterval = (start: Date, end: Date) => {
  return Math.ceil(Math.abs(differenceInMinutes(end, start) / 1440)); //1440 = 24 hours * 60 minutes
};

export const getDatesInDateTimeInterval = (start: Date, end: Date) => {
  const days = getDaysInDateTimeInterval(start, end);
  const length = days === 0 ? 1 : days;
  const result: Date[] = [];
  for (let i = 0; i < length; i++) {
    result.push(addDays(start, i));
  }
  return result;
};

export function formatDateOnlyValue(
  dateString: string | null | undefined,
  dateFormat: string = DATE_MASK,
  emptyValue: string = ""
) {
  if (dateString == null || String(dateString).trim() === "") {
    return emptyValue;
  }
  try {
    let date = parseDate(dateString, "yyyy-MM-dd", new Date());
    if (!isValid(date)) {
      date = new Date(dateString);
    }
    if (!isValid(date)) {
      return emptyValue;
    }
    return format(date, dateFormat);
  } catch {
    return emptyValue;
  }
}

export function formatBackendDateOnly(
  dateOnly: string = new Date().toISOString(),
  dateFormat: string = "yyyy-MM-dd",
  emptyValue: string = ""
) {
  if (dateOnly == null || String(dateOnly).trim() === "") {
    return emptyValue;
  }
  try {
    const dt = new Date(dateOnly);
    if (!isValid(dt)) {
      return emptyValue;
    }
    const year = dt.getFullYear();
    if (year < 1900 || year > 2100) return emptyValue;
    const dtOnlyDate = new Date(
      dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000
    );
    if (!isValid(dtOnlyDate)) {
      return emptyValue;
    }
    return format(dtOnlyDate, dateFormat);
  } catch {
    return emptyValue;
  }
}

export function formatLocalDateOnly(
  dateOnly: string = new Date().toISOString(),
  dateFormat: string = "yyyy-MM-dd"
) {
  const dt = new Date(dateOnly);
  if (isNaN(dt.getTime())) return "";

  // Extract UTC date components to preserve the intended date
  const year = dt.getUTCFullYear();
  const month = dt.getUTCMonth();
  const date = dt.getUTCDate();

  const localDate = new Date(year, month, date);
  return format(localDate, dateFormat);
}

export function parseBackendDateOnly(
  dateString: string = new Date().toISOString()
) {
  return parseDate(dateString, "yyyy-MM-dd", new Date());
}

export function strToDuration(duration: string) {
  const [hours, minutes, seconds] = duration?.split(":");
  return {
    hours: Number.parseInt(hours),
    minutes: Number.parseInt(minutes),
    seconds: Number.parseInt(seconds)
  } as Duration;
}

export function formatPhoneNumber(
  phoneNumber: string | undefined | null,
  format: string = "dash"
) {
  if (!phoneNumber) return "";
  const cleaned = String(phoneNumber ?? "").replace(/\D/g, "");
  if (!cleaned) return "";
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    if (format === "dash") {
      return `${match[1]}-${match[2]}-${match[3]}`;
    } else if (format === "parenthesis") {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
  }

  return null;
}

export function formatBirthdate(birthdate?: string | null) {
  if (!birthdate) return null;

  const date = parseDate(birthdate, "yyyy-MM-dd", new Date());

  return isValid(date) ? date : null;
}

export const formatBirthdateAsString = (
  birthdate?: string | null | Date,
  outputFormat = "MMM dd, yyyy"
) => {
  let date: Date;

  if (!birthdate) return "";
  if (birthdate instanceof Date) {
    date = birthdate;
  } else {
    if (birthdate) {
      date = parseDate(birthdate, "yyyy-MM-dd", new Date());
      if (!isValid(date)) {
        date = new Date(birthdate);
      }
    } else {
      return "";
    }
  }
  return format(date, outputFormat).replaceAll(" ", NON_BREAKABLE_SPACE);
};

export const formatMoneyValue = (value: number, isNegative?: boolean) => {
  return `${isNegative && value ? "-" : ""}${formatMoney(value, 2)}`;
};

export const formatMoney = (
  value: number,
  minimumFractionDigits?: number,
  maximumFractionDigits: number = 2,
  showSign: boolean = false,
  symbol: string = "$",
  direction: "before" | "after" = "before"
) => {
  const hasDecimals = value % 1 !== 0;
  const minDigits = minimumFractionDigits ?? (hasDecimals ? 2 : 0);
  const sign = showSign && value < 0 ? "-" : "";
  return `${sign}${direction === "before" ? symbol : ""}${Math.abs(
    value
  ).toLocaleString("en-US", {
    minimumFractionDigits: minDigits,
    maximumFractionDigits
  })}${direction === "after" ? symbol : ""}`;
};

export const calculatePercent = (
  part?: string,
  full?: string,
  max: number | undefined = undefined
) => {
  let result =
    part && Number(part || "0") > 0
      ? Math.round((Number(part || "0") / Number(full)) * 100 * 100) / 100
      : 0;

  if (max && result > max) return max;
  return result;
};

export const formatPercent = (
  value: number | "N/A",
  minimumFractionDigits: number = 0,
  maximumFractionDigits: number = 2
) =>
  value === "N/A"
    ? "N/A"
    : `${Math.abs(value).toLocaleString("en-US", {
        minimumFractionDigits,
        maximumFractionDigits
      })}%`;

export const formatMoneyWithNegativeValue = (
  value: number,
  minimumFractionDigits?: number,
  maximumFractionDigits: number = 2
) => {
  const hasDecimals = value % 1 !== 0;
  const minDigits = minimumFractionDigits ?? (hasDecimals ? 2 : 0);
  const stringValue = Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: minDigits,
    maximumFractionDigits
  });

  return value >= 0 ? `$${stringValue}` : `($${stringValue})`;
};

export const normalizeFloatingNumberCorrection = (value: number) =>
  Math.round(value * 100) / 100;

type PatientNameParts = {
  firstName: string;
  middleName?: string | null;
  preferredName?: string | null;
  lastName: string;
  title?: number | null;
  suffix?: string;
};

export const getFullName = ({
  firstName,
  middleName,
  preferredName,
  lastName,
  title,
  suffix
}: PatientNameParts) => {
  return [
    [
      TITLES.getFromCode(title),
      firstName,
      preferredName ? `(${preferredName})` : "",
      middleName,
      lastName
    ]
      .filter((namePart) => Boolean(namePart))
      .join(" "),
    suffix ? `, ${suffix}` : ""
  ].join("");
};

export function formatStaffMemberFullName<T>(
  staffMember: T & StaffMemberNameParts
): T {
  return {
    ...staffMember,
    fullName: getFullName({
      firstName: staffMember?.first_name ?? "",
      lastName: staffMember?.last_name ?? "",
      title: staffMember?.title,
      suffix: staffMember?.suffix
    })
  };
}

export function formatValidQueryParamUrlCommaSeparated(
  urlParams: {
    [param: string]: any;
  },
  addQuestionMark: boolean = false
) {
  const result = Object.keys(urlParams)
    .filter(
      (key: string) =>
        urlParams[key] !== undefined &&
        urlParams[key] !== null &&
        urlParams[key] !== "" &&
        urlParams[key]?.length !== 0
    )
    .map((key: string) => {
      if (Array.isArray(urlParams[key])) {
        return `${key}=` + urlParams[key].join(",");
      } else {
        return `${key}=${urlParams[key]}`;
      }
    })
    .join("&");

  if (addQuestionMark) {
    return isBlank(result) ? "?" : `?${result}`;
  }
  return result;
}

export function formatValidQueryParamUrl(urlParams: { [param: string]: any }) {
  return Object.keys(urlParams)
    .filter(
      (key: string) => urlParams[key] !== undefined && urlParams[key] !== null
    )
    .map((key: string) => {
      if (Array.isArray(urlParams[key])) {
        return urlParams[key]
          .map((value: string) => `${key}=${value}`)
          .join("&");
      } else {
        return `${key}=${urlParams[key]}`;
      }
    })
    .join("&");
}

export function getParentPath(pathname: string, stepsBack: number = 1) {
  const paths = pathname.split("/").filter((pathPart) => Boolean(pathPart));

  if (paths.length === 0) {
    return pathname;
  }
  return "/" + paths.slice(0, -stepsBack).join("/");
}

/**
 * Extracts the current route path from the URL without query parameters.
 * Ensures the path starts with "/" and doesn't end with "/" (except if it's just "/").
 *
 * @param url - Optional URL string. If not provided, uses window.location.href
 * @returns The normalized path (e.g., "/patients/7" or "/")
 *
 * @example
 * getCurrentRoutePath() // from http://localhost:3000/patients/7/ -> "/patients/7"
 * getCurrentRoutePath() // from http://localhost:3000/ -> "/"
 * getCurrentRoutePath("http://localhost:3000/patients/7?query=test") // -> "/patients/7"
 */
export function getCurrentRoutePath(url?: string): string {
  const urlToProcess = url || window.location.href;
  const urlObj = new URL(urlToProcess);
  let pathname = urlObj.pathname;

  // Remove trailing slash, except if pathname is just "/"
  if (pathname !== "/" && pathname.endsWith("/")) {
    pathname = pathname.slice(0, -1);
  }

  // Ensure it starts with "/"
  if (!pathname.startsWith("/")) {
    pathname = "/" + pathname;
  }

  return pathname;
}

export function getFieldName<FieldName extends string>(
  name: string,
  fieldName?: string
): FieldName {
  if (fieldName === undefined) return name as FieldName;
  return `${name}.${fieldName}` as FieldName;
}

type SimpleValueType = string | number | boolean;
type FlatObjectType = {
  [key: string]: SimpleValueType | SimpleValueType[];
};
type ComplexObjectType = {
  [key: string]:
    | SimpleValueType
    | ComplexObjectType
    | FlatObjectType
    | SimpleValueType[];
};
export type ObjectConversionInputType =
  | ComplexObjectType
  | SimpleValueType
  | SimpleValueType[];
export function camelCaseKeysToUnderscore(obj: ObjectConversionInputType) {
  if (typeof obj !== "object" || Array.isArray(obj)) return obj;

  for (const camelCaseName in obj) {
    // Camel to underscore
    const underscoreName = snakeCase(camelCaseName);

    // Only process if names are different
    if (underscoreName !== camelCaseName) {
      // Check for the old property name to avoid a ReferenceError in strict mode.
      if (obj.hasOwnProperty(camelCaseName)) {
        obj[underscoreName] = obj[camelCaseName];
        delete obj[camelCaseName];
      }
    }

    // Recursion
    if (typeof obj[underscoreName] === "object") {
      obj[underscoreName] = camelCaseKeysToUnderscore(obj[underscoreName]);
    }
  }
  return obj;
}

export function underScoreKeysToCamelCase(obj: ObjectConversionInputType) {
  if (typeof obj !== "object" || Array.isArray(obj)) return obj;

  for (const underscoreName in obj) {
    // Camel to underscore
    const camalcaseName = camelCase(underscoreName);

    // Only process if names are different
    if (camalcaseName !== underscoreName) {
      // Check for the old property name to avoid a ReferenceError in strict mode.
      if (obj.hasOwnProperty(underscoreName)) {
        obj[camalcaseName] = obj[underscoreName];
        delete obj[underscoreName];
      }
    }

    // Recursion
    if (typeof obj[camalcaseName] === "object") {
      obj[camalcaseName] = underScoreKeysToCamelCase(obj[camalcaseName]);
    }
  }
  return obj;
}

export const convertHtmlToText = (html: string | null | undefined) => {
  if (typeof html !== "string") return "";
  return decodeHTML(
    convertSunoPlaceholdersToMergeTags(html)
      // Remove head tag and its content
      .replace(/<head\b[^<]*(?:(?!<\/head>)<[^<]*)*<\/head>/gi, "")
      // Remove xml tag and its content
      .replace(/<xml\b[^<]*(?:(?!<\/xml>)<[^<]*)*<\/xml>/gi, "")
      // Remove script tags and their content
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
      // Remove style tags and their content (CSS)
      .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, "")
      // Convert br tags to newlines
      .replace(/<br\s*\/?>/gi, "\n")
      // Remove all remaining HTML tags
      .replace(/<[^>]+>/g, "")
      // Clean up extra horizontal whitespace (preserve newlines from br tags)
      .replace(/[ \t]+/g, " ")
      .trim()
  );
};

export const getHtmlAsTextPreview = (
  html: string | undefined | null,
  placeholder: string = "",
  length: number = 160
) => {
  if (typeof html !== "string") return placeholder;

  // Rich-text editors emit &nbsp; between words, which decodes to U+00A0. A browser never wraps
  // at one, so a preview made of them renders as a single unbreakable line and widens whatever
  // table column it sits in (ENG-2111). A preview has no layout to preserve, so plain spaces.
  const text = convertHtmlToText(html).replace(/ /g, " ").trim();
  if (!text) return placeholder;

  return truncate(text, {
    length,
    omission: "…"
  });
};

export function getParsedJson(str?: string | null): any {
  if (str === null || str === undefined || str === "") return null;
  if (str.includes("{") || str.includes("[")) {
    try {
      const parsed = JSON.parse(str);
      return typeof parsed === "object" && parsed !== null ? parsed : null;
    } catch (e) {
      return null;
    }
  }
  return null;
}

export function isJson(str?: string | null): boolean {
  return Boolean(getParsedJson(str));
}

export async function dataUrlToFile(
  dataUrl: string,
  fileName: string
): Promise<File> {
  const res: Response = await fetch(dataUrl);
  const blob: Blob = await res.blob();
  return new File([blob], fileName, { type: "image/png" });
}

export const isValidDate = (value: string | undefined | null, mask: string) => {
  return isValid(parseDate(value ?? "", mask, new Date()));
};

export const getValidDateOrDefault = (value: any, defaultValue: Date) => {
  if (isValid(value)) return value as Date;
  return defaultValue;
};

export const formatResourceUrl = (
  url?: string,
  cacheBustKey?: string | boolean
) => {
  if (!url || url.startsWith("blob:") || url.startsWith("data:")) return url;

  let formattedUrl: string;
  if (url.startsWith("/")) {
    formattedUrl = viteEnv.VITE_SUNO_API?.replace("/api", "").concat(url);
  } else {
    formattedUrl = url;
  }

  // Add cache busting parameter for PDF generation.
  //
  // A key derived from a field the caller's query does not request evaluates to
  // "" and disables cache busting entirely, leaving the PDF image src equal to
  // the raw stored URL. That regressed the intended PDF URL behavior and
  // coincided with the report-logo regression in SN-19177 / DEV-2640, so PDF
  // callers pass `true` for a per-call `Date.now()` key.
  //
  // The suspected failure mechanism, not confirmed against the asset host: our
  // <Image> downloads bytes with a CORS-mode fetch while a DOM <img> loads in
  // opaque no-CORS mode, so if an <img> loads the same URL first the browser may
  // cache a response with no CORS headers and then serve that entry to the fetch,
  // which fails the CORS check and embeds nothing. Some places do render the raw
  // stored logo URL in an <img> (the Practice Logo merge tag, the Box eDocs SDK's
  // logoUrl). #6348 hit the same class and worked around it with `no-store`.
  //
  // A stable version key (e.g. `clinic.updated_at`) is preferable for cache reuse,
  // but only where the source query actually returns that field, and ideally with
  // the PDF URL namespaced so it does not coincide with an <img> URL. Note that a
  // key is not appended when the URL already carries a matching `_cb`.
  if (cacheBustKey && formattedUrl) {
    const cacheParam = `${formattedUrl.includes("?") ? "&" : "?"}_cb=`;
    if (!formattedUrl.includes(cacheParam)) {
      const cacheValue =
        typeof cacheBustKey === "string" ? cacheBustKey : Date.now();
      formattedUrl += `${cacheParam}${cacheValue}`;
    }
  }

  return formattedUrl;
};

export const downloadCSVWithFormatter = (
  blob: Blob,
  fileName?: string,
  formatter?: (data: any[]) => any[]
) => {
  const reader = new FileReader();
  reader.onload = (event) => {
    const rawText = event.target?.result as string;
    const rawData = parse(rawText, { header: true, skipEmptyLines: true }).data;
    const formattedData = formatter ? formatter(rawData) : rawData;
    const csvData = unparse(formattedData, {
      quotes: true,
      quoteChar: '"',
      delimiter: ",",
      header: true
    });
    const csvBlob = new Blob([csvData], { type: "text/csv;charset=utf-8" });
    saveAs(csvBlob, fileName);
  };
  reader.readAsText(blob);
  return blob;
};

export function extractDecimalZeros(str: string) {
  const match = str.match(/\.(0+)?$/);
  return match ? match[0] : null;
}

export function isDateStringValid(date: any): boolean {
  const dt = typeof date === "string" ? new Date(date) : date;
  if (!isValid(dt) && !isValid(parseISO(dt))) return false;
  const year = (isValid(dt) ? dt : parseISO(dt)).getFullYear();
  return year >= 1900 && year <= 2100;
}

export function isDateRangeStringValid(fromDate: any, toDate: any) {
  return (
    isDateStringValid(fromDate) &&
    isDateStringValid(toDate) &&
    differenceInDays(new Date(toDate), new Date(fromDate)) >= 0
  );
}

export const nodeToJpeg = async (node: any, quality: number = 1) => {
  return htmlToImage.toJpeg(node, {
    backgroundColor: "#fff",
    quality,
    cacheBust: true,
    pixelRatio: 3,
    skipAutoScale: true,
    skipFonts: true
  });
};

export const nodeToPng = async (node: any) => {
  return htmlToImage.toPng(node, {
    backgroundColor: "#fff",
    cacheBust: true,
    pixelRatio: 3,
    skipAutoScale: true,
    skipFonts: true
  });
};
export const convertBlobUrlToFile = async (
  blob_url: string,
  fileName: string
) =>
  fetch(blob_url)
    .then((response) => response.blob())
    .then((blob) => {
      return new File([blob], fileName, { type: blob.type });
    });

export const isObject = (obj: any) => {
  return typeof obj === "object" && !Array.isArray(obj) && obj !== null;
};

const kilobyte: number = 1024;
const megabyte: number = kilobyte ** 2;
const gigabyte: number = kilobyte ** 3;

export const formatBytes = (byteSize: number, decimalPlaces = 0) => {
  if (byteSize < kilobyte) {
    return `${byteSize}B`;
  }
  if (byteSize < megabyte) {
    return `${(byteSize / kilobyte).toFixed(0)}KB`;
  }
  if (byteSize < gigabyte) {
    return `${(byteSize / megabyte).toFixed(decimalPlaces)}MB`;
  }

  return `${(byteSize / gigabyte).toFixed(decimalPlaces)}GB`;
};

export {
  calculateTaxRateValue,
  computeTaxAmount,
  computeTotalWithTax,
  roundTo,
  roundToTwo
} from "./math";

export function transformObjectPropertyValuesToStrings(obj: any) {
  return Object.keys(obj).reduce((acc, key) => {
    const value = obj[key];
    acc[key] =
      typeof value === "string" || typeof value === "number"
        ? String(value)
        : "";
    return acc;
  }, {} as any);
}

// The one CSV-escaping rule for client-side exports (RFC 4180): quote when the value
// carries a comma, quote, or line break, doubling embedded quotes. Shared so export
// surfaces cannot drift on escaping fixes.
export function escapeCsvValue(
  value: string | number | boolean | null | undefined
): string {
  if (value === null || value === undefined) {
    return "";
  }
  const stringValue = String(value);
  return /[",\n\r]/.test(stringValue)
    ? `"${stringValue.replace(/"/g, '""')}"`
    : stringValue;
}

export function downloadBlob(blob: Blob, fileName: string) {
  const fileUrl = window.URL.createObjectURL(blob);
  const tempLink = document.createElement("a");
  tempLink.href = fileUrl;
  tempLink.setAttribute("download", fileName);
  tempLink.setAttribute("target", "_blank");
  document.body.appendChild(tempLink);
  tempLink.click();
  document.body.removeChild(tempLink);
}

export function formatDurationValue(duration: string) {
  const [hours, minutes] = duration.split(":").map(Number);
  const durationInterval = intervalToDuration({
    start: 0,
    end: hours * 3600 * 1000 + minutes * 60 * 1000
  });

  return formatDuration(durationInterval);
}

export const deepEqualIgnoreIsNull = (object1: any, object2: any) => {
  const keys1 = Object.keys(omitBy(object1, isNil));
  const keys2 = Object.keys(omitBy(object2, isNil));

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const value1 = object1[key];
    const value2 = object2[key];

    if (value1 === null && value2 === null) {
      return true;
    }

    if (value1 === undefined && value2 === undefined) {
      return true;
    }

    if (typeof value1 === "object" && typeof value2 === "object") {
      if (!deepEqualIgnoreIsNull(value1, value2)) {
        return false;
      }
    } else if (value1 !== value2) {
      return false;
    }
  }

  return true;
};

/**
 * Shown when claimPrintTab returns null. Lives next to the primitive so the three print
 * paths cannot drift apart, and names *where* to act: a clinician who does not know what a
 * pop-up blocker is cannot find the setting from "allow pop-ups" alone.
 */
export const PRINT_TAB_BLOCKED_MESSAGE =
  "Your browser blocked the print window. Allow pop-ups for this site in the address bar, then press Print again.";

export type ClaimedPrintTab = {
  /** Hand the claimed tab its document and raise the print dialog once it has loaded. */
  print: (fileUrl: string) => Promise<void>;
  /** Give the tab back when the document never arrived. */
  close: () => void;
};

const PRINT_TAB_POLL_MS = 50;
const PRINT_TAB_POLL_LIMIT = 200;

export type PrintPdfTabOutcome =
  | "waiting"
  | "printed"
  | "left_to_viewer_extension"
  | "print_failed";

/**
 * The one place a print tab is checked and printed, shared by every print path. Call it
 * whenever the tab may have moved on; anything but "waiting" is final. Reading the tab
 * throws when it is not same-origin, and each caller decides what that means.
 *
 * Adobe Acrobat keeps the tab on the PDF's blob: URL but swaps the PDF for its own HTML
 * page, and print() on that page prints a blank sheet however long we wait (DEV-3480). Its
 * viewer has its own Print button, so we leave the printing to it and count the case in
 * RUM, once per print. The browsers' own viewers report "application/pdf" here (Chromium
 * and WebKit; Firefox refuses the read outright), so only Acrobat's measured "text/html"
 * counts: a browser that reports something unmeasured keeps its print dialog. The URL has
 * to match first, because the waiting page in front of the PDF is HTML too.
 */
export const tryPrintPdfTab = (
  printWindow: Window,
  fileUrl: string,
  printPath: string
): PrintPdfTabOutcome => {
  const { URL: documentUrl, readyState, contentType } = printWindow.document;

  if (documentUrl !== fileUrl) {
    return "waiting";
  }

  // Before readyState: Acrobat's page declares itself from the start, and catching it
  // while it is still loading its own assets keeps the count from missing a slow one.
  if (contentType === "text/html") {
    datadogRum.addAction("print_left_to_pdf_viewer_extension", { printPath });
    return "left_to_viewer_extension";
  }

  if (readyState !== "complete") {
    return "waiting";
  }

  try {
    printWindow.print();
    return "printed";
  } catch (error) {
    console.error("Printing failed:", error);
    return "print_failed";
  }
};

const escapeHtml = (value: string) =>
  value.replace(
    /[&<>"']/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;"
      })[character] as string
  );

// Painted into the claimed tab immediately, because the tab has to exist before the
// document does. Without it the user watches a blank about:blank page for as long as
// generation takes — seconds locally, longer over the network — and reasonably concludes
// the print produced an empty document. The <title> matters as much as the body: it is
// what the browser tab strip shows while waiting.
const placeholderDocument = (documentLabel: string) => `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>Preparing ${escapeHtml(documentLabel)}…</title>
<style>
  body { margin: 0; height: 100vh; display: flex; align-items: center;
         justify-content: center; background: #f6f8fa; color: #3c4257;
         font: 16px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
         sans-serif; }
  .spinner { width: 28px; height: 28px; margin: 0 auto 16px; border-radius: 50%;
             border: 3px solid #d7dde5; border-top-color: #5b6b7f;
             animation: spin 0.9s linear infinite; }
  .hint { margin-top: 8px; font-size: 13px; color: #6b7684; }
  /* Revealed by animation-delay rather than script: nothing in this tab can talk to the
     app, so the honest thing after a long wait is to stop implying progress is normal
     and hand the user a way out. Kept CSS-only so no policy or bundle can break it. */
  .slow { margin-top: 20px; max-width: 22rem; font-size: 13px; color: #6b7684;
          opacity: 0; animation: reveal 0s linear 30s forwards; }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes reveal { to { opacity: 1; } }
  @media (prefers-color-scheme: dark) {
    body { background: #1c1f24; color: #e6e9ee; }
    .spinner { border-color: #3a3f47; border-top-color: #9aa5b1; }
    .hint { color: #9aa5b1; }
  }
</style>
</head>
<body>
  <div>
    <div class="spinner"></div>
    <div>Preparing ${escapeHtml(documentLabel)}…</div>
    <div class="hint">The document will open here, ready to print.</div>
    <div class="slow">Still preparing. If nothing appears, close this tab and start the
    print again — nothing you entered has been lost.</div>
  </div>
</body>
</html>`;

/**
 * Claim a tab for printing. Call this SYNCHRONOUSLY inside the click handler, before the
 * first `await` — that is the whole point of it existing.
 *
 * A print that opens its tab after awaiting generation outlives the click's transient user
 * activation, so the browser blocks it. Measured in Chrome: neither `window.open` nor an
 * anchor with target="_blank" survives that, which is why `openFile`'s late-open path
 * cannot be rescued from inside `openFile`. Claiming up front also makes the failure
 * *observable* — a null here is a real pop-up preference, and the caller can say so.
 */
export function claimPrintTab(
  documentLabel: string = "your document"
): ClaimedPrintTab | null {
  const tab = window.open("", "_blank");

  if (!tab) {
    return null;
  }

  try {
    tab.document.write(placeholderDocument(documentLabel));
    tab.document.close();
  } catch (error) {
    // Cosmetic only: the document still arrives, the wait just looks blank.
    console.error("Could not paint the print tab placeholder:", error);
  }

  const print = async (fileUrl: string) => {
    try {
      tab.location.replace(fileUrl);
    } catch (error) {
      // The user closed the tab between claiming and generating.
      console.error("Could not hand the document to the print tab:", error);
      return;
    }

    // A load listener attached before that navigation does not survive it (measured in
    // both Chromium and WebKit), so the arriving document has to be observed instead.
    // `readyState` alone is not enough: for the first ~50ms the tab still holds
    // about:blank, which is already "complete" — printing then yields a blank page.
    // Matching document.URL is what proves the new document is the one on screen.
    for (let poll = 0; poll < PRINT_TAB_POLL_LIMIT; poll += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, PRINT_TAB_POLL_MS));

      if (tab.closed) {
        return;
      }

      let outcome: PrintPdfTabOutcome;
      try {
        outcome = tryPrintPdfTab(tab, fileUrl, "claim_print_tab");
      } catch {
        // Not same-origin, so the load cannot be observed. The user still has the
        // document open and prints from the viewer.
        return;
      }

      if (outcome !== "waiting") {
        return;
      }
    }
  };

  const close = () => {
    try {
      tab.close();
    } catch {
      // Nothing to do: the user may already have closed it.
    }
  };

  return { print, close };
}

export function openFile(
  fileUrl: string,
  shouldPrint: boolean = false,
  filename?: string
) {
  const tempLink = document.createElement("a");
  tempLink.href = fileUrl;
  tempLink.setAttribute("target", "_blank");

  // Set download attribute with filename to ensure proper file type and download behavior
  if (filename) {
    tempLink.setAttribute("download", filename);
  }

  if (shouldPrint) {
    tempLink.addEventListener("click", (e) => {
      // preventDefault only once window.open succeeded; cancelling first turned a blocked
      // open into a click that did nothing at all. The anchor fallback is not a rescue
      // though — measured in Chrome it is blocked too — so this branch mainly buys the
      // log line. Callers that need printing to actually work must claim their tab in the
      // click handler instead: see claimPrintTab above.
      const printWindow = window.open(fileUrl, "_blank");

      if (!printWindow) {
        console.error(
          "Print window blocked (user activation likely expired); the anchor fallback is not reliable"
        );
        return;
      }

      e.preventDefault();

      printWindow.onload = () => {
        let outcome: PrintPdfTabOutcome;
        try {
          outcome = tryPrintPdfTab(printWindow, fileUrl, "open_file");
        } catch (error) {
          // A window we cannot read cannot be printed from here either: print() threw
          // the same way before this went through tryPrintPdfTab.
          console.error("Printing failed:", error);
          outcome = "print_failed";
        }

        if (outcome === "print_failed") {
          // Fallback to just opening the file if printing fails
          printWindow.close();
          window.open(fileUrl, "_blank");
        }
      };
    });
  }

  document.body.appendChild(tempLink);
  tempLink.click();
  document.body.removeChild(tempLink);
}

export const formatDaysDisplay = (
  duration: number | null | undefined
): string => {
  if (duration === null || duration === 0) return "";
  return duration === 1 ? "1 day" : `${duration} days`;
};

export const getPathNameWithoutEndingSlash = (pathName: string) => {
  return pathName.endsWith("/") ? pathName.slice(0, -1) : pathName;
};

export const getAgeInMonthsOrYears = (
  age_years: string | number | null | undefined,
  birthdate: string | null | undefined,
  asOfDate?: string | number | Date | null
) => {
  const dateOfBirth = birthdate
    ? typeof birthdate === "string"
      ? parseDate(birthdate, "yyyy-MM-dd", new Date())
      : new Date(birthdate)
    : null;

  if (asOfDate && dateOfBirth && isValid(dateOfBirth)) {
    const referenceDate = new Date(asOfDate);
    if (isValid(referenceDate)) {
      const yearsAge = differenceInYears(referenceDate, dateOfBirth);
      if (yearsAge >= 1) {
        return yearsAge;
      }
      const monthsAge = differenceInMonths(referenceDate, dateOfBirth);
      return monthsAge
        ? `${monthsAge} month${monthsAge !== 1 ? "s" : ""}`
        : yearsAge;
    }
  }

  const patientDateOfBirth = dateOfBirth ? format(dateOfBirth, DATE_MASK) : "";

  const monthsAge =
    !Number(age_years) && patientDateOfBirth
      ? differenceInMonths(new Date(), new Date(patientDateOfBirth))
      : "";

  const monthsAgeText = monthsAge
    ? `${monthsAge} month${monthsAge !== 1 ? "s" : ""}`
    : "";
  return monthsAgeText ? monthsAgeText : age_years;
};

export function getDateDifferenceInHumanFormat(
  startDate: Date,
  endDate: Date,
  postfix?: string
): string {
  const duration = intervalToDuration({
    start: min([startDate, endDate]),
    end: max([startDate, endDate])
  });

  if (
    !duration.years &&
    !duration.weeks &&
    !duration.months &&
    !duration.days &&
    !duration.hours &&
    !duration.minutes &&
    (!duration.seconds || duration.seconds < 60)
  ) {
    return "now";
  }

  return `${formatDuration(omit(duration, "seconds"))
    .replace(/minute[s]?/i, "min")
    .replace(/ hour[s]?/i, "h")}${postfix !== undefined ? " " + postfix : ""}`;
}

// `sanitizeHTML` was extracted to its own module back when `common.ts` read Vite's
// `import.meta` directly (unparseable by Jest; env access now goes through
// utils/vite-env.util). The re-export below preserves the existing
// `import { sanitizeHTML } from "utils/common"` call sites.
export { sanitizeHTML } from "./sanitize-html.util";

export const getSelectedIds = (ids: Record<number, boolean>) => {
  return Object.entries(ids || {})
    .filter(([id, selected]) => selected)
    .map(([id]) => String(id));
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Returns full data URL including MIME type (e.g. "data:image/png;base64,ABC123...")
      resolve(reader.result as string);
    };
    reader.onerror = (error) => reject(error);
  });
};

export const base64ToFile = (
  dataUrl: string,
  filename: string
): File | undefined => {
  // Parse the data URL to extract MIME type and base64 data
  const matches = dataUrl.match(/^data:(.+?);base64,(.+)$/);
  if (!matches || matches.length !== 3) {
    console.error(
      "Invalid data URL format. Expected format: data:<mime-type>;base64,<data>"
    );
    return undefined;
  }

  const mimeType = matches[1];
  const base64Data = matches[2];

  // Convert Base64 to binary using atob
  const byteCharacters = atob(base64Data);
  const byteArrays = new Uint8Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteArrays[i] = byteCharacters.charCodeAt(i);
  }

  // Create and return the File object
  const blob = new Blob([byteArrays], { type: mimeType });
  return new File([blob], filename, { type: mimeType });
};

export const urlToBase64 = async (
  url: string,
  retries = 1,
  timeoutMs?: number,
  // Pass "no-store" when the same URL is also rendered in an <img> on the
  // page: the cached <img> response has no CORS headers (image loads skip
  // CORS), so a fetch() served from that cache entry gets blocked. Bypassing
  // the HTTP cache forces a fresh request with an Origin header.
  fetchCache?: RequestCache
): Promise<string | null> => {
  if (!url) return null;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;

  try {
    const controller = new AbortController();
    const timeoutId =
      timeoutMs !== undefined
        ? setTimeout(() => controller.abort(), timeoutMs)
        : undefined;

    const response = await fetch(url, {
      signal: controller.signal,
      ...(fetchCache ? { cache: fetchCache } : {})
    });
    if (timeoutId) clearTimeout(timeoutId);
    if (!response.ok) return null;

    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    if (retries > 0) {
      await new Promise((r) => setTimeout(r, 1000));
      return urlToBase64(url, retries - 1, timeoutMs, fetchCache);
    }
    console.error("Error converting URL to base64:", error);
    return null;
  }
};

export async function blobToUint8Array(blob: Blob) {
  const arrayBuffer = await blob.arrayBuffer();
  return new Uint8Array(arrayBuffer);
}

export function isDefaultPhoneNumber(phone: string): boolean {
  if (!phone) return false;

  const digits = phone.replace(/\D/g, "");

  return digits.length > 0 && /^0+$/.test(digits);
}
